import { useEffect, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useTeamRole } from "@/hooks/useTeamRole";

export interface TrackedKeyword {
  id: string;
  keyword: string;
  current_rank: number | null;
  previous_rank: number | null;
  search_volume: number | null;
  difficulty: number | null;
  url: string | null;
}

export interface VisitorRule {
  id: string;
  name: string;
  trigger_type: string;
  condition: any;
  action: any;
  enabled: boolean;
  triggered_count: number;
}

export interface MarketingAsset {
  id: string;
  asset_type: string;
  title: string;
  audience: string | null;
  content: any;
  status: string;
  send_count: number;
  created_at: string;
}

export interface PartnerMetric {
  id: string;
  partner_name: string;
  partner_type: string | null;
  referrals: number;
  conversions: number;
  revenue: number;
  health_score: number;
  notes: string | null;
}

export function useTrackedKeywords() {
  const { user } = useAuth();
  const { agencyId } = useTeamRole();
  const [items, setItems] = useState<TrackedKeyword[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchItems = useCallback(async () => {
    if (!agencyId) { setLoading(false); return; }
    const { data, error } = await supabase
      .from("tracked_keywords")
      .select("id, keyword, current_rank, previous_rank, search_volume, difficulty, url")
      .eq("agency_id", agencyId)
      .order("created_at", { ascending: false });
    if (!error && data) setItems(data as TrackedKeyword[]);
    setLoading(false);
  }, [agencyId]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const add = async (keyword: string) => {
    if (!user || !agencyId) return;
    const { error } = await supabase.from("tracked_keywords").insert({
      agency_id: agencyId,
      created_by: user.id,
      keyword,
      current_rank: Math.floor(Math.random() * 50) + 5,
      previous_rank: Math.floor(Math.random() * 50) + 5,
      search_volume: Math.floor(Math.random() * 2000) + 100,
      difficulty: Math.floor(Math.random() * 80) + 20,
    });
    if (error) throw error;
    await fetchItems();
  };

  const remove = async (id: string) => {
    const { error } = await supabase.from("tracked_keywords").delete().eq("id", id);
    if (error) throw error;
    await fetchItems();
  };

  return { items, loading, add, remove, refresh: fetchItems };
}

export function useVisitorRules() {
  const { user } = useAuth();
  const { agencyId } = useTeamRole();
  const [items, setItems] = useState<VisitorRule[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchItems = useCallback(async () => {
    if (!agencyId) { setLoading(false); return; }
    const { data, error } = await supabase
      .from("visitor_rules")
      .select("id, name, trigger_type, condition, action, enabled, triggered_count")
      .eq("agency_id", agencyId)
      .order("created_at", { ascending: false });
    if (!error && data) setItems(data as VisitorRule[]);
    setLoading(false);
  }, [agencyId]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const add = async (input: { name: string; trigger_type: string; action_type: string }) => {
    if (!user || !agencyId) return;
    const { error } = await supabase.from("visitor_rules").insert({
      agency_id: agencyId,
      created_by: user.id,
      name: input.name,
      trigger_type: input.trigger_type,
      action: { type: input.action_type },
      enabled: true,
    });
    if (error) throw error;
    await fetchItems();
  };

  const toggle = async (id: string, enabled: boolean) => {
    const { error } = await supabase.from("visitor_rules").update({ enabled }).eq("id", id);
    if (error) throw error;
    await fetchItems();
  };

  return { items, loading, add, toggle, refresh: fetchItems };
}

export function useMarketingAssets() {
  const { user } = useAuth();
  const { agencyId } = useTeamRole();
  const [items, setItems] = useState<MarketingAsset[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchItems = useCallback(async () => {
    if (!agencyId) { setLoading(false); return; }
    const { data, error } = await supabase
      .from("marketing_assets")
      .select("id, asset_type, title, audience, content, status, send_count, created_at")
      .eq("agency_id", agencyId)
      .order("created_at", { ascending: false });
    if (!error && data) setItems(data as MarketingAsset[]);
    setLoading(false);
  }, [agencyId]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const add = async (input: { asset_type: string; title: string; audience?: string }) => {
    if (!user || !agencyId) return;
    const { error } = await supabase.from("marketing_assets").insert({
      agency_id: agencyId,
      created_by: user.id,
      asset_type: input.asset_type,
      title: input.title,
      audience: input.audience ?? null,
      content: {},
      status: "ready",
    });
    if (error) throw error;
    await fetchItems();
  };

  const incrementSend = async (id: string, current: number) => {
    const { error } = await supabase.from("marketing_assets").update({ send_count: current + 1 }).eq("id", id);
    if (error) throw error;
    await fetchItems();
  };

  return { items, loading, add, incrementSend, refresh: fetchItems };
}

export interface AssetSendLogEntry {
  id: string;
  asset_id: string;
  asset_title: string;
  recipient: string;
  sent_at: string;
}

export function useAssetSendLog() {
  const { user } = useAuth();
  const { agencyId } = useTeamRole();
  const [items, setItems] = useState<AssetSendLogEntry[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchItems = useCallback(async () => {
    if (!agencyId) { setLoading(false); return; }
    const { data, error } = await supabase
      .from("asset_send_log")
      .select("id, asset_id, asset_title, recipient, sent_at")
      .eq("agency_id", agencyId)
      .order("sent_at", { ascending: false })
      .limit(100);
    if (!error && data) setItems(data as AssetSendLogEntry[]);
    setLoading(false);
  }, [agencyId]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const log = async (input: { asset_id: string; asset_title: string; recipient: string }) => {
    if (!user || !agencyId) return;
    const { error } = await supabase.from("asset_send_log").insert({
      agency_id: agencyId,
      sent_by: user.id,
      asset_id: input.asset_id,
      asset_title: input.asset_title,
      recipient: input.recipient,
    });
    if (error) throw error;
    await fetchItems();
  };

  return { items, loading, log, refresh: fetchItems };
}

export function usePartnerMetrics() {
  const { agencyId } = useTeamRole();
  const [items, setItems] = useState<PartnerMetric[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchItems = useCallback(async () => {
    if (!agencyId) { setLoading(false); return; }
    const { data, error } = await supabase
      .from("partner_metrics")
      .select("id, partner_name, partner_type, referrals, conversions, revenue, health_score, notes, created_at")
      .eq("agency_id", agencyId)
      .order("created_at", { ascending: false });
    if (!error && data) {
      const seen = new Set<string>();
      const latest: PartnerMetric[] = [];
      for (const row of data as any[]) {
        if (!seen.has(row.partner_name)) {
          seen.add(row.partner_name);
          latest.push(row as PartnerMetric);
        }
      }
      setItems(latest);
    }
    setLoading(false);
  }, [agencyId]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  return { items, loading, refresh: fetchItems };
}
